import { Star } from 'lucide-react'
import * as React from 'react'

import { cn } from '../lib/utils'

const STARS = [1, 2, 3, 4, 5] as const

export interface RatingProps {
  /** Current rating, 0–5. Fractional values render partial fill in display mode. */
  value: number
  /** Star size in px. Default 15 — the inline/list size; 22 reads as the primary control. */
  size?: number
  /** Let the user set the rating. Renders a radiogroup instead of a static image. */
  interactive?: boolean
  /** Fires with the chosen star, or `0` when the user clears by re-activating the current one. */
  onChange?: (value: number) => void
  /**
   * Accessible name for the control — "Rating, 4.8 out of 5" when static, "Rate this plan" when
   * interactive. Already-translated; this package stays i18n-agnostic.
   *
   * It matters more than it looks: without it a static rating is a row of decorative glyphs that
   * screen readers skip entirely, so the rating simply isn't there for those users.
   */
  label: string
  /**
   * Names each star for assistive tech, e.g. `(n) => t('rate.stars', { count: n })`. Defaults to
   * the bare number, which is language-neutral but reads plainly ("3, radio button, 3 of 5").
   */
  starLabel?: (star: number) => string
  /**
   * Appended to the CURRENT star's accessible name, e.g. "3 stars, activate to clear". Without it
   * the clear gesture is invisible to assistive tech — the interaction exists but nothing says so.
   * Also shown as that star's tooltip, which is the only hint a sighted pointer user gets.
   */
  clearHint?: string
  className?: string
}

/**
 * The app's star rating, in two modes.
 *
 * **Static** (default) renders fractional fill — 4.8 fills the last star ~80% — by clipping a
 * filled star over an outline, and exposes itself as a single labelled image. **Interactive**
 * (`interactive`) is a proper radiogroup built on real radio inputs, so whole-star selection,
 * arrow-key navigation and single-tab-stop behaviour come from the browser, with a focus ring on
 * each star.
 *
 * Extracted from the cooking-history plan card, where it was a local component with no focus
 * state and a `role="radiogroup"` whose children were plain buttons — so assistive tech got a
 * group that reported no options and no selection.
 */
export function Rating({ value, size = 15, interactive = false, onChange, label, starLabel, clearHint, className }: RatingProps) {
  const [hover, setHover] = React.useState(0)
  // Radios group by `name`, so each Rating needs its own — otherwise two on one page would
  // fight over a single selection.
  const groupName = React.useId()
  const shown = interactive && hover ? hover : value

  const base = cn('inline-flex items-center gap-0.5 text-amber', className)

  // One star: an outline with a filled copy clipped to `fill` (0–1) laid over it.
  const star = (fill: number) => (
    <span className="relative inline-flex">
      {/* `amber-l`, not `amber/40` — `--es-*` has no `<alpha-value>`, so the modifier compiled to
          nothing and empty stars inherited full-strength amber. */}
      <Star size={size} className="text-amber-soft" strokeWidth={1.5} />
      {fill > 0 && (
        <span className="absolute inset-0 overflow-hidden" style={{ width: `${fill * 100}%` }}>
          <Star size={size} className="fill-amber text-amber" strokeWidth={1.5} />
        </span>
      )}
    </span>
  )

  if (!interactive) {
    // A single labelled image, not five unlabelled glyphs — `role="img"` stops assistive tech
    // walking into the decorative spans and lets the whole thing announce as one value.
    return (
      <span role="img" aria-label={label} className={base}>
        {STARS.map((i) => (
          <span key={i} className="inline-flex">
            {star(Math.max(0, Math.min(1, shown - (i - 1))))}
          </span>
        ))}
      </span>
    )
  }

  // Exactly one radio may be checked, so a fractional value rounds to its nearest whole star.
  const checked = Math.round(value)

  // Real `<input type="radio">`s sharing a name, not buttons wearing `role="radio"`. The browser
  // then supplies the entire APG radiogroup keyboard contract for free and more reliably than a
  // hand-rolled one: arrow keys move and select, the group is a single tab stop, focus enters on
  // the checked option (or the first when nothing is checked). They're `sr-only`, so the stars
  // stay the visible control and the focus ring is drawn by the label on their behalf.
  //
  // `has-[:focus-visible]`, NOT `focus-within`: focus-within fires for any focus, so clicking a
  // star left a ring sitting on it afterwards — which no rating control does, and which reads as
  // "stuck". Focus SHOULD stay on the clicked star (that's what lets you carry on with the arrow
  // keys); only the ring is keyboard-only. `:focus-visible` draws exactly that line, and `has-[]`
  // is how the label reads the state of the sr-only input inside it — the same pattern the
  // link-focus and rule-row surfaces already use.
  return (
    <span role="radiogroup" aria-label={label} className={base} onMouseLeave={() => setHover(0)}>
      {STARS.map((i) => (
        <label
          key={i}
          onMouseEnter={() => setHover(i)}
          title={checked === i ? clearHint : undefined}
          className="inline-flex cursor-pointer rounded-sm transition-transform hover:scale-110 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-pelorous-500 has-[:focus-visible]:ring-offset-2 has-[:focus-visible]:ring-offset-background motion-reduce:transition-none motion-reduce:hover:scale-100 dark:has-[:focus-visible]:ring-pelorous-300">
          {/* A radio group has no native deselect — once one is checked, nothing unchecks it. So
              clearing is re-activating the current star, and it takes THREE handlers, because the
              browser gives us nothing for the repeat case:
                - `change` fires only when the checkedness CHANGES, so it covers picking a
                  different star and arrow-key navigation, and never fires for a repeat.
                - `click` DOES fire when you click the already-checked radio — that's the pointer
                  clear.
                - `keydown` is needed because Space on an already-checked radio dispatches no
                  click and no change: the browser treats it as a no-op. Without this the clear
                  would be pointer-only, which is its own accessibility bug. Verified in Chromium
                  rather than assumed — the first cut of this shipped exactly that gap. */}
          <input
            type="radio"
            name={groupName}
            value={i}
            checked={checked === i}
            onClick={() => {
              if (checked === i) onChange?.(0)
            }}
            onKeyDown={(e) => {
              if (checked === i && (e.key === ' ' || e.key === 'Enter')) {
                e.preventDefault()
                onChange?.(0)
              }
            }}
            onChange={() => onChange?.(i)}
            className="sr-only"
          />
          <span className="sr-only">
            {starLabel ? starLabel(i) : String(i)}
            {checked === i && clearHint ? `, ${clearHint}` : ''}
          </span>
          {star(shown >= i ? 1 : 0)}
        </label>
      ))}
    </span>
  )
}
